import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { MatButton } from "@angular/material/button";
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSliderModule } from '@angular/material/slider';
import { MatTooltip } from "@angular/material/tooltip";
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ClientService } from '../../../services/client.service';
import { DeleteModalComponent } from "../../../shared/delete-modal/delete-modal.component";

@Component({
  selector: 'app-onebox-modal',
  imports: [
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    ReactiveFormsModule,
    MatDatepickerModule,
    MatSelectModule,
    CommonModule,
    MatNativeDateModule,
    MatCheckboxModule,
    MatSliderModule,
    MatButton,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltip,
    DeleteModalComponent
],
  templateUrl: './onebox-modal.component.html',
  styleUrl: './onebox-modal.component.scss',
  providers: [
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { subscriptSizing: 'dynamic' } }
  ]
})
export class OneboxModalComponent implements OnInit, OnDestroy {
  action: 'add' | 'edit' | 'delete' | undefined;
  formData!: FormGroup;
  isLoading = false;

  selectAliasOptions: { value: string; label: string }[] = [];
  size: { value: number; label: string }[] = Array.from({ length: 10 }, (_, i) => ({ value: i + 1, label: String(i + 1) }));

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly dialogRef: MatDialogRef<OneboxModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {
      action: 'add' | 'edit' | 'delete',
      payload: any,
      selectAliasOptions: { value: string; label: string }[]
    },
    private readonly fb: FormBuilder,
    private readonly clientService: ClientService
  ) { }

  ngOnInit(): void {
    this.action = this.data.action;

    // chiamata per recuperare gli alias
    this.clientService.getAllAlias().subscribe(response => {
      const aliasOptions = response as Array<{ value: string; label: string }>;
      this.selectAliasOptions = aliasOptions.map((x: any) => ({
        value: x.id,
        label: x.name
      }));
    });

    const payload = this.data.payload || {};
    console.log('payload', payload);

    this.formData = this.fb.group({
      name: [payload.name || null, Validators.required],
      description: [payload.description || null, Validators.required],
      triggerType: [payload.triggerType || null, Validators.required],
      providerType: [payload.providerType || null, Validators.required],
      regexText: [payload.regexText || null],
      url: [payload.url || null],
      aliasId: [payload.providerType === 'INTERNAL' ? payload.aliasId : null],
      size: [payload.size || null, Validators.required],
      flagMetadati: [payload.flagMetadati ?? false],
      extraInputKeywordArray: this.fb.array(
        (payload.extraInputKeywordArray || []).map((item: any) => this.fb.group({
          idOneBox: [item.idOneBox ?? null],
          idKeyword: [item.idKeyword ?? null],
          keyword: [item.keyword || null, Validators.required]
        }))
      )
    });
    
    this.setupConditionalFields();
  }

  private setupConditionalFields(): void {
    
    this.formData.get('triggerType')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(
      value =>  {
        const regexCtrl = this.formData.get('regexText');

        if(value === 'REGEX') {
          regexCtrl?.setValidators([Validators.required])
        } else {
          regexCtrl?.clearValidators();
          regexCtrl?.setValue(null);
        }

        if(value !== 'KEYWORDS'){
          this.clearExtraInputs();
        }

        regexCtrl?.updateValueAndValidity();
      });

      this.formData.get('providerType')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(
        value => {
          const urlCtrl = this.formData.get('url');
          const aliasCtrl = this.formData.get('aliasId');
          const metadatiCtrl = this.formData.get('flagMetadati');

          if(value === 'EXTERNAL'){
            console.log('ext');
            urlCtrl?.setValidators([Validators.required]);
            aliasCtrl?.reset();
            metadatiCtrl?.setValue(false);
          } else {
            console.log('int');
            urlCtrl?.clearValidators();
            urlCtrl?.setValue(null);
          }

          urlCtrl?.updateValueAndValidity();
          aliasCtrl?.updateValueAndValidity();
        });
  }

  get extraInputs(): FormArray {
    return this.formData.get('extraInputKeywordArray') as FormArray;
  }

  setExtraInputs(keywords: any[]) {
    const controls = keywords.map(item => this.fb.group({
      idOneBox: [item.idOneBox ?? null],
      idKeyword: [item.idKeyword ?? null],
      keyword: [item.keyword ?? '']
    }));
    this.formData.setControl('extraInputKeywordArray', this.fb.array(controls));

    this.isLoading = false;
  }

  addExtraInput() {
    this.extraInputs.push(this.fb.group({ keyword: ['', Validators.required] }));
  }

  removeExtraInput(index: number) {
    this.extraInputs.removeAt(index);
  }

  clearExtraInputs() {
    this.extraInputs.clear();
  }

  get title(): string {
      switch (this.action) {
        case 'add': return 'Aggiungi';
        case 'edit': return 'Modifica';
        case 'delete': return 'Elimina';
        default: return '';
      } 
  }

  onModalClose(): void {
    this.dialogRef.close();
  }

  onModalSave(): void {
    console.log('formdata', this.formData.value);
    if (this.formData.invalid && this.action !== 'delete') {
      console.log('invalido');
      return;
    }
    let dataToPass: any;

    if (this.action === 'delete') {
      dataToPass = this.data.payload;
    } else if (this.action === 'edit') {
      if (this.data.payload.id) {
        dataToPass = { ...this.formData.value, id: this.data.payload.id };
      } else {
        dataToPass = { ...this.formData.value };
      }

    } else if (this.action === 'add') {
      dataToPass = this.formData.value;
    }

    this.dialogRef.close(dataToPass);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
